import "./Events.css";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDMY } from "../../utils/date";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { CheckRegistrationStatus } from "../../apis/api/EventsApi";
import Cookies from "js-cookie";

const fadeUp  = { hidden:{opacity:0,y:20}, show:{opacity:1,y:0} };
const stagger = { show:{ transition:{ staggerChildren:.1 } } };

const cleanText = (t = "") =>
  t.replace(/&nbsp;/g," ").replace(/<[^>]*>/g,"").trim();

const Events = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const events    = location.state?.Events || [];
  const eventArr  = Array.isArray(events) ? events : [events];
  const profile   = useSelector(s => s.ProfileSlice?.profile || s.ProfileSlice || {});
  const companyId = Cookies.get("company_id");

  const [registrationStatus, setRegistrationStatus] = useState({});
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const checkAllRegistrations = async () => {
      if (!companyId) {
        setCheckingStatus(false);
        return;
      }

      const statusMap = {};
      
      for (const event of eventArr) {
        try {
          const result = await dispatch(CheckRegistrationStatus({
            event_id: event.id,
            company_id: companyId
          })).unwrap();
          
          statusMap[event.id] = {
            registered: result.registered || false,
            status: result.registration_status || null
          };
        } catch (error) {
          console.error(`Error checking registration for event ${event.id}:`, error);
          statusMap[event.id] = { registered: false, status: null };
        }
      }
      
      setRegistrationStatus(statusMap);
      setCheckingStatus(false);
    };

    checkAllRegistrations();
  }, [eventArr, companyId, dispatch]);

  const handleRegister = (event) => {
    navigate("/EventRegistration", {
      state: {
        event,
        prefill: {
          salutation: profile.salutation || "",
          first_name: profile.first_name || "",
          last_name: profile.last_name || "",
          email: profile.email || "",
          mobile: profile.contact || "",
          designation: profile.job_title || "",
        }
      }
    });
  };

  const isRegistered = (eventId) => {
    return registrationStatus[eventId]?.registered || false;
  };

  const getRegistrationStatusText = (eventId) => {
    const status = registrationStatus[eventId]?.status;
    if (!status) return null;
    
    const statusMap = {
      'pending':  'Under Review',
      'approved': 'Registration Approved',
      'rejected': 'Currently Unavailable'
    };
    
    return statusMap[status] || status;
  };

  return (
    <motion.div className="ed-wrap"
      variants={stagger} initial="hidden" animate="show">
      {eventArr.map((event) => (
        <motion.div key={event.id} className="ed-card-container" variants={fadeUp}
          transition={{ duration:.45, ease:[.22,.68,0,1.2] }}>

          {/* Hero */}
          <div className="ed-hero">
            <img src={event.file_url?.replace("/thumb","")}
              className="ed-hero-img" alt={event.Event_Name}
              onError={(e) => { e.target.style.display="none"; }} />
            <div className="ed-hero-overlay" />
            <div className="ed-hero-body">
              <span className="ed-hero-badge">
                <i className="bx bx-calendar-event"></i>
                {event.status === "current" ? "Live Event" : "Past Event"}
              </span>
              <h1 className="ed-hero-title">{event.Event_Name}</h1>
              {event.Event_Title && (
                <p className="ed-hero-sub">{event.Event_Title}</p>
              )}
            </div>
          </div>

          {/* Info cards */}
          <motion.div className="ed-info-grid"
            variants={stagger} initial="hidden" animate="show">
            <motion.div className="ed-info-card" variants={fadeUp}>
              <span className="ed-info-icon"><i className="bx bx-calendar-check"></i></span>
              <div>
                <p className="ed-info-label">Start Date</p>
                <p className="ed-info-val">{formatDMY(event.start_date) || "—"}</p>
              </div>
            </motion.div>
            <motion.div className="ed-info-card" variants={fadeUp}>
              <span className="ed-info-icon"><i className="bx bx-calendar-x"></i></span>
              <div>
                <p className="ed-info-label">End Date</p>
                <p className="ed-info-val">{formatDMY(event.end_date) || "—"}</p>
              </div>
            </motion.div>
            <motion.div className="ed-info-card" variants={fadeUp}>
              <span className="ed-info-icon"><i className="bx bx-map-pin"></i></span>
              <div>
                <p className="ed-info-label">Location / Timezone</p>
                <p className="ed-info-val">
                  {event.Location?.split(":")[1] || event.Location || "—"}
                  {event.timeZone ? ` · ${event.timeZone}` : ""}
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Description */}
          <motion.div className="ed-desc-card" variants={fadeUp}>
            <h2 className="ed-desc-title">
              <i className="bx bx-detail"></i> Event Description
            </h2>
            <p className="ed-desc-text">
              {cleanText(event.description) || "No description available."}
            </p>
          </motion.div>

          {/* Register button - only for upcoming/live events (end_date in the future) */}
          {Number(event.end_date) >= Date.now() / 1000 && (
            <motion.div className="ed-register-wrap" variants={fadeUp}>
              {checkingStatus ? (
                <div className="ed-status-card checking">
                  <i className="bx bx-loader-alt bx-spin"></i>
                  <span>Checking status...</span>
                </div>
              ) : isRegistered(event.id) ? (
                <div className={`ed-status-card registered ${registrationStatus[event.id]?.status}`}>
                  <i className={`bx ${
                    registrationStatus[event.id]?.status === 'approved' ? 'bx-check-circle' :
                    registrationStatus[event.id]?.status === 'rejected' ? 'bx-info-circle' :
                    'bx-time-five'
                  }`}></i>
                  <span>{getRegistrationStatusText(event.id)}</span>
                </div>
              ) : (
                <button className="ed-reg-btn" onClick={() => handleRegister(event)}>
                  <i className="bx bx-calendar-plus"></i>
                  Register for Event
                </button>
              )}
            </motion.div>
          )}

        </motion.div>
      ))}
    </motion.div>
  );
};

export default Events;
